import React from 'react';
import PropTypes from 'prop-types';
import SubSearch from 'client/components/SubSearch/SubSearch.react';
import {LegacyFindAndApply, CheckboxChip, Button, Text} from '@albert-io/atomic';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {parseSearchString} from 'client/components/AdvancedSearch/AdvancedSearch.utils';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {List} from 'immutable';

const TAGS_PER_PAGE = 250;

export default class TagsSubSearch extends React.Component {
  static propTypes = {
    subjectId: PropTypes.string.isRequired,
    guideType: PropTypes.string.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      additionalResults: List(),
      currentPage: 1,
      hasMore: false,
      isLoadingMore: false,
      lastSearchString: '',
      lastInitialResults: List(),
      loadError: null
    };
    // Use refs to track render-time data without triggering re-renders
    this.latestRenderData = {
      results: List(),
      searchString: ''
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const {results, searchString} = this.latestRenderData;
    const {lastSearchString, lastInitialResults} = this.state;

    // Check if we have new data from the last render that needs processing
    if (
      results.size > 0 &&
      (searchString !== lastSearchString || !results.equals(lastInitialResults))
    ) {
      const isSearchChange = searchString !== lastSearchString;

      this.setState({
        additionalResults: isSearchChange ? List() : this.state.additionalResults,
        currentPage: isSearchChange ? 1 : this.state.currentPage,
        hasMore: results.size >= TAGS_PER_PAGE,
        lastSearchString: searchString,
        lastInitialResults: results,
        loadError: isSearchChange ? null : this.state.loadError
      });
    }
  }

  makeQuery = (searchString, page = 1) => {
    const {keywords, phrases, isSearchingForKeywords, isSearchingForPhrases} =
      parseSearchString(searchString);

    return resource('search_tags_v1')
      .sort('name')
      .mandarkEndpoint(['search_tags_v1'])
      .filter({
        question_sets_v1: {
          subject_v2: {
            id: this.props.subjectId
          },
          guide_levels_v2: {
            nlevel: 1,
            guide_v1: {
              guide_type: this.props.guideType
            }
          },
          questions_v3: {
            status: 'published' // Only include published questions
          }
        }
      })
      .filter(
        {
          document_stems: {
            search_tokens: encodeURIComponent(keywords)
          }
        },
        isSearchingForKeywords
      )
      .filter(
        {
          document: {
            search_phrases: encodeURIComponent(phrases)
          }
        },
        isSearchingForPhrases
      )
      .pageSize(TAGS_PER_PAGE)
      .page(page);
  };

  handleLoadMore = async (searchString) => {
    this.setState({isLoadingMore: true, loadError: null});

    try {
      const nextPage = this.state.currentPage + 1;
      const query = this.makeQuery(searchString, nextPage);
      const newResults = await query.getResourcePromise();

      // Check if search hasn't changed while we were loading
      if (searchString === this.state.lastSearchString) {
        this.setState((prevState) => ({
          additionalResults: prevState.additionalResults.concat(newResults),
          currentPage: nextPage,
          hasMore: newResults.size >= TAGS_PER_PAGE,
          isLoadingMore: false
        }));
      } else {
        // Search changed, discard these results
        this.setState({isLoadingMore: false});
      }
    } catch (error) {
      console.error('Error loading more tags:', error);
      this.setState({
        isLoadingMore: false,
        loadError: 'Failed to load more tags. Please try again.'
      });
    }
  };

  render() {
    return (
      <SubSearch
        {...this.props}
        className='advanced-search__sub-search advanced-search__sub-search--tags'
        queryFunc={(searchString) => this.makeQuery(searchString, 1)}
        allowEmptyQuery
        renderResultsFunc={({onCheck, results, selectedItems, searchString}) => {
          // Store latest render data in ref - componentDidUpdate will process it
          this.latestRenderData = {results, searchString};

          // Combine initial results from SubSearch with additional loaded results
          const allResults = results.concat(this.state.additionalResults);

          return (
            <LegacyFindAndApply.Body className='advanced-search__sub-search__results'>
              {allResults.map((result) => (
                <CheckboxChip
                  data-id={result.getId()}
                  icon='tag'
                  key={result.getId()}
                  onChange={(e) => onCheck(e, result)}
                  checked={selectedItems.has(result.getId())}
                >
                  <MarkdownRendererV2 text={result.getName()} />
                </CheckboxChip>
              ))}
              {this.state.loadError && (
                <div className='p-x2 text-center'>
                  <Text color='negative' size='s'>
                    {this.state.loadError}
                  </Text>
                </div>
              )}
              {this.state.hasMore && !this.state.loadError && (
                <div className='p-x2'>
                  <Button
                    variant='outlined'
                    onClick={() => this.handleLoadMore(searchString)}
                    disabled={this.state.isLoadingMore}
                    className='w-full'
                  >
                    {this.state.isLoadingMore ? 'Loading...' : 'Load more tags'}
                  </Button>
                </div>
              )}
            </LegacyFindAndApply.Body>
          );
        }}
      />
    );
  }
}
